/** @jsxImportSource @emotion/react */
import { useState, useEffect, useCallback } from 'react'
import { ManageAccounts } from '@mui/icons-material'
import {
  Box,
  Drawer,
  Table,
  TableHead,
  Typography,
  useTheme,
  TableCell,
  TableRow,
  TableBody,
  IconButton,
  TextField,
} from '@mui/material'

import { Icon } from '@app/components/Atoms/Icon'
import { toast } from '@app/components/ui/Toast/toast'
import { Button } from '@app/components/Atoms/Button'
import { TempRolesSelector } from './Admin.TempRolesSelector'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'

import {
  useCreateCustomUserRole,
  useDeleteCustomUserRole,
  useUpdateCustomUserRole,
} from '../hooks/useCustomUserRoles'
import { UserCustomRole } from '@app/types'
import { DEFAULT_ROLES } from '../utils/Admin.constants'

import {
  drawerWrapper,
  formTitleStyle,
  rolesFormWrapper,
  formTextFieldStyle,
  drawerActionsStyle,
  formDescriptionStyle,
  drawerInner,
  loadingWrapper,
  table,
  roleRow,
  descriptionRow,
  editButtonRow,
  defaultBodyRow,
  roleBodyRow,
  descriptionBodyRow,
  editBodyRow,
  deleteBodyRow,
} from '../styles/Admin.styles'

type ManageUserRolesDrawerProps = {
  opensUserRolesDrawer: boolean
  setOpensUserRolesDrawer: (open: boolean) => void
  customRoles: UserCustomRole[] | undefined
  customRolesLoading: boolean
}

export const ManageUserRolesDrawer = ({
  opensUserRolesDrawer,
  setOpensUserRolesDrawer,
  customRoles,
  customRolesLoading,
}: ManageUserRolesDrawerProps) => {
  const theme = useTheme()
  const updateCustomUserRolesMutation = useUpdateCustomUserRole()
  const createCustomUserRolesMutation = useCreateCustomUserRole()
  const deleteCustomUserRolesMutation = useDeleteCustomUserRole()
  const mutationPending =
    createCustomUserRolesMutation.isPending ||
    updateCustomUserRolesMutation.isPending ||
    deleteCustomUserRolesMutation.isPending

  const [newUserRoleName, setNewUserRoleName] = useState('')
  const [creatingUserRole, setCreatingUserRole] = useState(false)
  const [editUserRole, setEditUserRole] = useState<null | number>(null)
  const [newUserRoleDescription, setNewUserRoleDescription] = useState('')

  const newRoleNameValidation = useCallback(
    (roleId?: number) => {
      const trimmedName = newUserRoleName.toLowerCase().trim()
      return (
        (!!newUserRoleName &&
          customRoles?.some((role) => role.name === newUserRoleName && role.id !== roleId)) ||
        trimmedName === 'admin' ||
        trimmedName === 'member' ||
        trimmedName === 'viewer'
      )
    },
    [newUserRoleName, customRoles],
  )

  function handleCreateUpdateUserRole(id?: number) {
    if (!newUserRoleName) {
      toast({
        description: 'Role name is required.',
        position: 'top-right',
      })
      return
    }
    if (newRoleNameValidation(id)) {
      toast({
        description: 'Role name must be unique from existing roles.',
        position: 'top-right',
      })
      return
    }

    if (editUserRole) {
      updateCustomUserRolesMutation.mutate({
        id: editUserRole,
        name: newUserRoleName,
        description: newUserRoleDescription,
      })
      setEditUserRole(null)
    } else {
      createCustomUserRolesMutation.mutate({
        name: newUserRoleName,
        description: newUserRoleDescription,
      })
    }
  }

  useEffect(() => {
    if (createCustomUserRolesMutation.isSuccess || updateCustomUserRolesMutation.isSuccess) {
      setNewUserRoleName('')
      setNewUserRoleDescription('')
    }
  }, [createCustomUserRolesMutation.isSuccess, updateCustomUserRolesMutation.isSuccess])

  return (
    <Drawer
      aria-labelledby="manage-users-drawer-title"
      anchor="right"
      open={opensUserRolesDrawer}
      onClose={() => setOpensUserRolesDrawer(false)}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: '700px' },
          },
        },
      }}
    >
      <Box css={drawerWrapper}>
        <Box css={drawerInner}>
          {mutationPending ? (
            <Box css={loadingWrapper}>
              <LoadingSpinner />
            </Box>
          ) : null}
          <Typography
            id="manage-user-drawer-title"
            variant="h5"
            css={formTitleStyle(theme.palette.primary.dark)}
          >
            Manage Roles
          </Typography>
          <Typography variant="body1" css={formDescriptionStyle(theme.palette.primary.dark)}>
            View role permissions, create custom permissions.
          </Typography>
          {customRolesLoading ? <Box>Loading user roles...</Box> : null}
          <Table css={table}>
            <TableHead>
              <TableRow>
                <TableCell css={roleRow}>Role Name</TableCell>
                <TableCell css={descriptionRow}>Description</TableCell>
                <TableCell css={editButtonRow}>Edit</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DEFAULT_ROLES.map((role) => (
                <TableRow key={role.name}>
                  <TableCell css={defaultBodyRow}>{role.name}</TableCell>
                  <TableCell>{role.description}</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))}
              {customRoles?.map((role) => (
                <TableRow key={role.name}>
                  <TableCell css={roleBodyRow}>
                    {editUserRole === role.id ? (
                      <TextField
                        css={{ width: '88px' }}
                        placeholder="Name"
                        name="userRoleName"
                        aria-describedby="userRoleNameHelp"
                        value={newUserRoleName}
                        onChange={(e) => setNewUserRoleName(e.target.value)}
                        error={newRoleNameValidation(editUserRole)}
                      />
                    ) : (
                      role.name
                    )}
                  </TableCell>
                  <TableCell css={descriptionBodyRow}>
                    {editUserRole === role.id ? (
                      <TextField
                        css={formTextFieldStyle}
                        placeholder="Description (Optional)"
                        name="userRoleDescription"
                        aria-describedby="userRoleDescriptionHelp"
                        value={newUserRoleDescription}
                        onChange={(e) => setNewUserRoleDescription(e.target.value)}
                      />
                    ) : (
                      role.description
                    )}
                  </TableCell>
                  <TableCell css={editBodyRow} align="center">
                    {editUserRole === role.id ? (
                      <>
                        <IconButton
                          onClick={() => {
                            handleCreateUpdateUserRole(editUserRole)
                          }}
                          disabled={updateCustomUserRolesMutation.isPending}
                        >
                          <Icon iconName="SaveIcon" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setEditUserRole(null)
                          }}
                        >
                          <Icon iconName="Close" />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => {
                          setCreatingUserRole(false)
                          setNewUserRoleName(role.name)
                          setNewUserRoleDescription(role.description || '')
                          setEditUserRole(role.id)
                        }}
                        disabled={updateCustomUserRolesMutation.isPending}
                      >
                        <Icon iconName="EditIcon" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell css={deleteBodyRow} align="center">
                    <IconButton
                      onClick={() => deleteCustomUserRolesMutation.mutate({ id: role.id })}
                      disabled={deleteCustomUserRolesMutation.isPending}
                    >
                      <Icon iconName="Delete" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {creatingUserRole ? (
            <Box css={rolesFormWrapper}>
              <TextField
                css={formTextFieldStyle}
                placeholder="Name"
                name="userRoleName"
                aria-describedby="userRoleNameHelp"
                value={newUserRoleName}
                onChange={(e) => setNewUserRoleName(e.target.value)}
                error={newRoleNameValidation()}
              />
              <TextField
                css={formTextFieldStyle}
                placeholder="Description (Optional)"
                name="userRoleDescription"
                aria-describedby="userRoleDescriptionHelp"
                value={newUserRoleDescription}
                onChange={(e) => setNewUserRoleDescription(e.target.value)}
              />
            </Box>
          ) : null}
          <Box css={drawerActionsStyle}>
            {!creatingUserRole ? (
              <>
                <Button variant="outlined" onClick={() => setOpensUserRolesDrawer(false)}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setCreatingUserRole(true)
                    setEditUserRole(null)
                    setNewUserRoleName('')
                    setNewUserRoleDescription('')
                  }}
                  startIcon={<ManageAccounts />}
                >
                  Create Role
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" onClick={() => setCreatingUserRole(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleCreateUpdateUserRole()}
                  startIcon={<ManageAccounts />}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </Box>
        <TempRolesSelector customRoles={customRoles} />
      </Box>
    </Drawer>
  )
}
